import { useState, useEffect } from 'react';
import { topicsAPI, departmentsAPI } from '../lib/api';
import api from '../lib/api';
import { LoadingPage, PageHeader, Modal, Alert, Spinner } from '../components/shared';
import { Plus, BookOpen, Users, Clock, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const DOMAINS = ['Power Systems','Control Systems','Electronics','Communications','Computer Engineering','Biomedical Engineering','Renewable Energy','Signal Processing','Electromagnetics','Embedded Systems','IoT','Robotics','Other'];

const emptyForm = { title: '', description: '', domain: '', required_skills: '', max_students: 1, supervision_mode: 'remote', expected_outcomes: '', department_id: '' };

const statusColor = s => ({ pending_approval: 'var(--warning)', open: 'var(--teal)', sponsored: 'var(--gold)', rejected: 'var(--error)', closed: 'var(--text-muted)' }[s] || 'var(--text-muted)');

export default function AlumniTopicsPage() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deletionModal, setDeletionModal] = useState(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionSubmitting, setDeletionSubmitting] = useState(false);

  const handleRequestDeletion = async () => {
    if (!deletionReason.trim()) { toast.error('Please provide a reason.'); return; }
    setDeletionSubmitting(true);
    try {
      await api.post(`/topics/${deletionModal.id}/request-deletion`, { reason: deletionReason });
      toast.success('Deletion request submitted. Admin will review shortly.');
      setDeletionModal(null); setDeletionReason(''); load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request.');
    } finally { setDeletionSubmitting(false); }
  };

  const load = () => {
    setLoading(true);
    topicsAPI.getMy()
      .then(r => setTopics(r.data || []))
      .catch(() => setTopics([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); departmentsAPI.getAll().then(r => setDepartments(r.data)).catch(() => {}); }, []);

  const handleSubmit = async () => {
    setError('');
    if (!form.title || !form.description || !form.domain) { setError('Title, description and domain are required.'); return; }
    setSubmitting(true);
    try {
      await topicsAPI.create({
        ...form,
        required_skills: form.required_skills.split(',').map(s => s.trim()).filter(Boolean),
        max_students: parseInt(form.max_students) || 1,
      });
      toast.success('Topic submitted for admin approval.');
      setShowForm(false); setForm(emptyForm); load();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || 'Failed to submit topic.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <LoadingPage />;

  return (
    <div>
      <PageHeader
        eyebrow="Alumni"
        title="Topics I've Posted"
        subtitle="Post research topics you want students to work on — you fund and mentor the project"
        actions={<button className="btn btn-primary" onClick={() => setShowForm(true)}><Plus size={16} /> Post a Topic</button>}
      />

      {topics.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <BookOpen size={40} style={{ color: 'var(--gold)', margin: '0 auto 16px', opacity: 0.5 }} />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400, marginBottom: 8 }}>No topics posted yet</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 24 }}>Post a research topic to connect with talented students.</p>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}><Plus size={16} /> Post Your First Topic</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {topics.map(topic => (
            <div key={topic.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>{topic.domain}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 500, marginBottom: 8 }}>{topic.title}</h3>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>{topic.description}</p>
                  <div style={{ display: 'flex', gap: 16, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    <span><Users size={13} style={{ verticalAlign: 'middle' }} /> {topic.application_count || 0} applicants</span>
                    <span><Users size={13} style={{ verticalAlign: 'middle' }} /> Max {topic.student_slots || topic.max_students} students</span>
                  </div>
                  {topic.required_skills?.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {topic.required_skills.map((s, i) => (
                        <span key={i} style={{ background: 'rgba(45,212,191,0.08)', color: 'var(--teal)', border: '1px solid rgba(45,212,191,0.15)', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  <span style={{ padding: '4px 12px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, background: `${statusColor(topic.status)}22`, color: statusColor(topic.status), textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>{topic.status?.replace('_', ' ')}</span>
                  {!topic.is_sponsored && !topic.deletion_requested && ['pending_approval','open','rejected'].includes(topic.status) && (
                    <button className="btn btn-ghost btn-sm" onClick={() => { setDeletionModal(topic); setDeletionReason(''); }}
                      style={{ color: 'var(--error)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Trash2 size={13} /> Request Deletion
                    </button>
                  )}
                  {topic.deletion_requested && (
                    <span style={{ fontSize: '0.72rem', color: 'var(--warning)', background: 'rgba(245,158,11,0.1)', padding: '2px 8px', borderRadius: 4 }}>
                      Deletion pending admin review
                    </span>
                  )}
                </div>
              </div>
              {topic.admin_feedback && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: '0.875rem', color: 'var(--error)' }}>
                  <strong>Admin feedback:</strong> {topic.admin_feedback}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={showForm} onClose={() => { setShowForm(false); setError(''); setForm(emptyForm); }} title="Post a Research Topic">
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Topic Title *</label>
            <input className="form-input" placeholder="e.g. AI-Powered Grid Fault Detection" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Department *</label>
            <select className="form-select" value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
              <option value="">Select department…</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Domain *</label>
            <select className="form-select" value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}>
              <option value="">Select domain…</option>
              {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea className="form-textarea" rows={4} placeholder="Describe the research problem, objectives, and what you hope to achieve…" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Expected Outcomes</label>
            <textarea className="form-textarea" rows={2} placeholder="What should the student produce or deliver?" value={form.expected_outcomes} onChange={e => setForm(f => ({ ...f, expected_outcomes: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Required Skills <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(comma-separated)</span></label>
            <input className="form-input" placeholder="e.g. Python, MATLAB, Circuit Design" value={form.required_skills} onChange={e => setForm(f => ({ ...f, required_skills: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Max Students</label>
              <input className="form-input" type="number" min={1} max={5} value={form.max_students} onChange={e => setForm(f => ({ ...f, max_students: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Supervision Mode</label>
              <select className="form-select" value={form.supervision_mode} onChange={e => setForm(f => ({ ...f, supervision_mode: e.target.value }))}>
                <option value="remote">Remote</option>
                <option value="in_person">In Person</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-ghost" onClick={() => { setShowForm(false); setError(''); setForm(emptyForm); }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Submitting…' : 'Post Topic'}</button>
          </div>
        </div>
      </Modal>
      <Modal open={!!deletionModal} onClose={() => { setDeletionModal(null); setDeletionReason(''); }} title="Request Topic Deletion">
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
          You are requesting deletion of <strong style={{ color: 'var(--text-primary)' }}>{deletionModal?.title}</strong>. Admin will review your request before removing the topic.
        </p>
        <div className="form-group" style={{ marginBottom: 20 }}>
          <label className="form-label">Reason for deletion *</label>
          <textarea className="form-textarea" rows={3} placeholder="Explain why this topic should be removed…"
            value={deletionReason} onChange={e => setDeletionReason(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => { setDeletionModal(null); setDeletionReason(''); }}>Cancel</button>
          <button onClick={handleRequestDeletion} disabled={deletionSubmitting}
            style={{ background: 'var(--error)', color: 'white', border: 'none', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', fontWeight: 500 }}>
            {deletionSubmitting ? <Spinner size={14} /> : 'Submit Request'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
